import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LaasOption } from 'src/app/core/models/laas-option';
import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';

import { PoServiceService } from '../../services/po-service.service';
import { DeliveryOrderService } from '../../services/delivery-order.service';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { endOfDay, startOfDay } from 'date-fns';
import { AdvisingServiceService } from '../../services/advising-service.service';
import { SearchIQCDetailRequest } from 'src/app/core/models/transport-operation/search-iqc-detail-request';

@Component({
  selector: 'laas-iqc-status-search',
  templateUrl: './iqc-status-search.component.html',
  styleUrls: ['./iqc-status-search.component.scss']
})
export class IQCStatusSearchComponent extends SessionBaseComponent implements OnInit {

  readonly permissionCode: PermissionConstants = PermissionConstants.ViewIQCStatusSearch;
  currentLang: string = this.laasSetting.language;
  readonly dayRange: number = 7;
  defaultArrivalDt: Date[];

  @ViewChild('dataTable') dataTable!: DataTableComponent;
  @ViewChild('arrivalDt') arrivalDt!: CalendarComponent;

  isSearchFormOpen: boolean = true;
  searchForm: FormGroup;
  isLoading: boolean = false;
  columns: any[];
  selectedRows: any[] = [];
  searchParams: any;
  submitted: boolean = false;
  searchResults: any[] = [];

  // Dropdown options
  statusOptions: Array<LaasOption> = [];
  specLocTypeOptions: Array<LaasOption> = [];

  constructor(
    private fb: FormBuilder,
    injector: Injector,
    private dropdownOptionsService: DropdownOptionsService,
    private poServiceService: PoServiceService,
    private doService: DeliveryOrderService,
    private advisingService: AdvisingServiceService
  ) {
    super(injector);

    this.defaultArrivalDt = [startOfDay(new Date().addDays(this.dayRange * -1)), endOfDay(new Date())];

    this.searchParams = {
      plant: null,
      specLocType: [],
      sku: null,
      statusList: [],
      arrivalDtStart: this.defaultArrivalDt[0],
      arrivalDtEnd: this.defaultArrivalDt[1]
    };

    this.searchForm = this.fb.group({
      plant: [null, Validators.required],
      specLocType: [[]],
      sku: [null],
      statusList: [[]],
      arrivalDtStart: [this.defaultArrivalDt[0]],
      arrivalDtEnd: [this.defaultArrivalDt[1]]
    });

    this.columns = [
      { field: 'externReceiptKey', header: 'IQCStatusSearch.MainTable.ExternReceiptKey', sort: true },
      { field: 'specLocType', header: 'IQCStatusSearch.MainTable.SpecLocType', sort: true },
      { field: 'sku', header: 'IQCStatusSearch.MainTable.SKU', sort: true },
      { field: 'qtyExpected', header: 'IQCStatusSearch.MainTable.QtyExpected', sort: true },
      { field: 'qtyReceived', header: 'IQCStatusSearch.MainTable.QtyReceived', sort: true },
      { field: 'currentStatus', header: 'IQCStatusSearch.MainTable.CurrentStatus', sort: true },
      { field: 'receiptCreatedTime', header: 'IQCStatusSearch.MainTable.ReceiptCreatedTime', sort: true, dataType: 'date' },
      { field: 'iqcTime', header: 'IQCStatusSearch.MainTable.IQCTime', sort: true, dataType: 'date' },
      { field: 'iqcFinishTime', header: 'IQCStatusSearch.MainTable.IQCFinishTime', sort: true, dataType: 'date' },
      { field: 'udFinishTime', header: 'IQCStatusSearch.MainTable.UDFinishTime', sort: true, dataType: 'date' },
      { field: 'receiveDuration', header: 'IQCStatusSearch.MainTable.ReceiveDuration', sort: true },
      { field: 'iqcDuration', header: 'IQCStatusSearch.MainTable.IQCDuration', sort: true },
      { field: 'udDuration', header: 'IQCStatusSearch.MainTable.UDDuration', sort: true },
      { field: 'totalDuration', header: 'IQCStatusSearch.MainTable.TotalDuration', sort: true },
      { field: 'remark', header: 'IQCStatusSearch.MainTable.Remark', sort: true }
    ];
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // Initialize status options
    this.statusOptions = [
      { label: '待收貨', value: 10 },
      { label: '待檢驗', value: 20 },
      { label: '待入庫', value: 30 },
      { label: '完成', value: 40 },
      { label: '異常', value: 99 }
    ];

    // Load specLocType options (儲區別)
    this.doService.getSpecLocType().subscribe({
      next: (result) => {
        const seenSpecLocTypes = new Set<string>();
        this.specLocTypeOptions = result.specLocTypeList
          .filter((x: { specLocType: string; }) => {
            if (seenSpecLocTypes.has(x.specLocType)) return false;
            seenSpecLocTypes.add(x.specLocType);
            return true;
          })
          .map((x: { specLocType: any; }) => ({
            label: x.specLocType,
            value: x.specLocType
          }));
      }
    });
  }

  searchBtnClick() {
    this.dataTable?.setCurrentPage(0);
    this.searchResult();
  }

  searchResult() {
    this.isLoading = true;
    this.selectedRows.length = 0;

    // Validate required fields
    if (this.searchForm.invalid) {
      this.submitted = true;
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
      this.isLoading = false;
      return;
    }

    this.searchParams = this.searchForm.value;

    // Prepare request to match backend structure
    const request: SearchIQCDetailRequest = {
      specLocTypeList: this.searchParams.specLocType || [],
      sku: this.searchParams.sku,
      statusList: this.searchParams.statusList || [],
      arriveDtStart: this.searchParams.arrivalDtStart,
      arriveDtEnd: this.searchParams.arrivalDtEnd,
      plant: this.searchParams.plant
    };

    this.advisingService.searchIQCDetail(request)
      .subscribe({
        next: result => {
          this.isSearchFormOpen = false;
          this.searchResults = [];

          if (!isNullOrEmpty(result) && !isNullOrEmpty(result.inboundStatusList)) {
            this.searchResults = deepCopy(result.inboundStatusList) ?? [];
          }

          // Convert dates and status
          for (const row of this.searchResults) {
            if (!isNullOrEmpty(row.receiptCreatedTime)) {
              row.receiptCreatedTime = new Date(row.receiptCreatedTime);
            }
            if (!isNullOrEmpty(row.iqcTime)) {
              row.iqcTime = new Date(row.iqcTime);
            }
            if (!isNullOrEmpty(row.iqcFinishTime)) {
              row.iqcFinishTime = new Date(row.iqcFinishTime);
            }
            if (!isNullOrEmpty(row.udFinishTime)) {
              row.udFinishTime = new Date(row.udFinishTime);
            }
            // Convert status code to display text
            if (!isNullOrEmpty(row.currentStatus)) {
              row.currentStatus = this.convertStatusCodeToText(row.currentStatus);
            }
          }

          this.dataTable.updateTableData(this.searchResults);
          this.isLoading = false;
        },
        error: (err: any) => {
          this.searchResults = [];
          this.dataTable.updateTableData(this.searchResults);
          this.toastErrorMessage(err.error);
          this.isLoading = false;
        }
      });
  }

  resetBtnClick(event: any) {
    this.arrivalDt.ngOnInit();

    this.searchForm.reset({
      plant: null,
      specLocType: [],
      sku: null,
      statusList: [],
      arrivalDtStart: this.defaultArrivalDt[0],
      arrivalDtEnd: this.defaultArrivalDt[1]
    });
  }

  downloadExcelBtn($event: Event) {
    this.isLoading = true;

    // Validate required fields
    if (this.searchForm.invalid) {
      this.submitted = true;
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
      this.isLoading = false;
      return;
    }

    this.searchParams = this.searchForm.value;

    // Prepare request to match backend structure
    const request: SearchIQCDetailRequest = {
      specLocTypeList: this.searchParams.specLocType || [],
      sku: this.searchParams.sku,
      statusList: this.searchParams.statusList || [],
      arriveDtStart: this.searchParams.arrivalDtStart,
      arriveDtEnd: this.searchParams.arrivalDtEnd,
      plant: this.searchParams.plant
    };

    this.advisingService.exportIQCDetail(request).subscribe({
      next: result => {
        if (result == null || result.size == 0) {
          this.translateService.get('Common.Toast.NoData').subscribe({
            next: i18nResult => {
              this.toastService.info(i18nResult);
            }
          });
          this.isLoading = false;
        } else {
          const file = new Blob([result], { type: 'application/vnd.ms-excel' });
          const fileName = "IQC_Detail_" + new Date().toISOString().slice(0, 10).replace(/-/g, '') + ".xls";

          const url = URL.createObjectURL(file);
          let a: any = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
          this.isLoading = false;
        }
      },
      error: (err: any) => {
        this.toastErrorMessage(err.error);
        this.isLoading = false;
      }
    });
  }

  dateOnChange(date: any, type: string) {
    if (type === 'arrivalDt') {
      // Validate date range: startDate should not be greater than endDate
      if (date.startDate && date.endDate && date.startDate > date.endDate) {
        this.toastService.warn(this.translateService.instant('Common.Message.StartDateCannotBeGreaterThanEndDate'));
        // Reset to default dates
        this.arrivalDt.ngOnInit();
        this.searchForm.controls.arrivalDtStart.setValue(this.defaultArrivalDt[0]);
        this.searchForm.controls.arrivalDtEnd.setValue(this.defaultArrivalDt[1]);
        return;
      }
      // Convert to full day range (startOfDay for start, endOfDay for end)
      const arrivalDtStart = date.startDate ? startOfDay(date.startDate) : null;
      const arrivalDtEnd = date.endDate ? endOfDay(date.endDate) : null;
      this.searchForm.controls.arrivalDtStart.setValue(arrivalDtStart);
      this.searchForm.controls.arrivalDtEnd.setValue(arrivalDtEnd);
    }
  }

  /**
   * Convert status code to display text
   * @param statusCode - The numeric status code from backend
   * @returns The corresponding display text
   */
  convertStatusCodeToText(statusCode: number): string {
    const statusMap: { [key: number]: string } = {
      10: '待收貨',
      20: '待檢驗',
      30: '待入庫',
      40: '完成',
      99: '異常'
    };
    return statusMap[statusCode] || statusCode.toString();
  }
}
